// Calendar.tsx
import { useFormUtils } from "@/app/hooks/useFormUtils";
import { useStringConstants } from "@/app/hooks/useStringConstants";
import { Dialog, Transition } from "@headlessui/react";
import { CalendarDaysIcon, ChevronLeftIcon, ChevronRightIcon } from "@heroicons/react/20/solid";
import clsx from "clsx";
import { addDays, format, getDate, getMonth, getYear, isToday, subDays } from "date-fns";
import React, { useState } from "react";
import FormFieldErrors from "../../Reusable/FormFieldErrors";
import { IPWLCalendar } from "../../interface";

type ByDateOptionFilter = "today" | "yesterday" | "tomorrow" | "custom";

const RotaCalendarComponent: React.FC<IPWLCalendar> = ({
  selectedDate = new Date(),
  onSelectDate,
  errorMessages,
  showLeftNav,
  fieldError,
  showTypedErrors,
  allowPastDaySelection,
  showFutureYears,
  displayDateString,
  nextWeekHandelFunction,
  prevWeekHandelFunction,
  label,
}) => {
  const currentTodayDate = new Date(Date.now());
  const { deduceFormFieldErrors } = useFormUtils();
  const constants = useStringConstants();
  const [selectedByDateOption, setSelectedByDateOption] = useState<ByDateOptionFilter>("custom");
  const [currentSelectedDate, setCurrentSelectedDate] = useState<Date>(currentTodayDate);
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const [selectedYear, setSelectedYear] = useState<number | null>(getYear(selectedDate));
  const [isOpen, setIsOpen] = useState(false);

  const toggleCalendar = () => {
    setIsOpen(!isOpen);
  };

  const byDateFilterOptions = [
    {
      title: "Today",
      id: "today",
      callbackFunc: () => {
        setCurrentSelectedDate(currentTodayDate);
        setCurrentMonth(currentTodayDate);
        setSelectedYear(getYear(currentTodayDate));
        setSelectedByDateOption("today");
        onSelectDate(currentTodayDate);
        toggleCalendar();
      },
    },
    {
      title: "Tomorrow",
      id: "tomorrow",
      callbackFunc: () => {
        const tomorrowDate = addDays(currentTodayDate, 1);
        setCurrentSelectedDate(tomorrowDate);
        setCurrentMonth(tomorrowDate);
        setSelectedYear(getYear(tomorrowDate));
        setSelectedByDateOption("tomorrow");
        onSelectDate(tomorrowDate);
        toggleCalendar();
      },
    },
    {
      title: "Yesterday",
      id: "yesterday",
      callbackFunc: () => {
        const yesterdayDate = subDays(currentTodayDate, 1);
        setCurrentSelectedDate(yesterdayDate);
        setCurrentMonth(yesterdayDate);
        setSelectedYear(getYear(yesterdayDate));
        setSelectedByDateOption("yesterday");
        onSelectDate(yesterdayDate);
        toggleCalendar();
      },
    },
  ];

  let calendarYears = allowPastDaySelection ? getYear(new Date()) - 1900 : 1;

  // Adjust to show future years if required
  calendarYears = showFutureYears ? calendarYears + 10 : calendarYears;

  // Ensure calendarYears includes at least the year 2024
  calendarYears = Math.max(calendarYears, 2024 - 1900);

  console.log(calendarYears);

  const renderHeader = () => {
    return (
      <div className="flex justify-between items-center mb-2">
        <div className="relative">
          <div className="w-full mt-2 flex justify-start items-center overflow-hidden">
            <div className="p-2">
              <select
                className="w-full outline-none border-none text-xs border rounded pr-2 focus:outline-none"
                value={selectedYear ?? ""}
                onChange={(e) => {
                  const year = parseInt(e.target.value, 10);
                  setSelectedYear(year === -1 ? null : year);
                }}
              >
                <option value={-1}>Select Year</option>
                {Array.from({ length: calendarYears }, (_, index) => {
                  if (showFutureYears) {
                    return getYear(new Date()) + index;
                  } else {
                    return getYear(new Date()) - index;
                  }
                }).map((year) => (
                  <option key={year} value={year}>
                    {year}
                  </option>
                ))}
              </select>
            </div>
            <div className="p-2">
              <select
                className="w-28 text-xs border outline-none border-none rounded pr-2 focus:outline-none"
                value={getMonth(currentMonth)}
                onChange={(e) => {
                  const newMonth = parseInt(e.target.value, 10);
                  setCurrentMonth((prev) => new Date(selectedYear ?? getYear(prev), newMonth, 1));
                }}
              >
                <option value={-1}>Select Month</option>
                {Array.from({ length: 12 }, (_, index) => index).map((month) =>
                  allowPastDaySelection ? (
                    <option key={month} value={month}>
                      {format(new Date(selectedYear ?? getYear(new Date()), month, 1), "MMMM")}
                    </option>
                  ) : (
                    month >= getMonth(new Date()) && (
                      <option key={month} value={month}>
                        {format(new Date(selectedYear ?? getYear(new Date()), month, 1), "MMMM")}
                      </option>
                    )
                  ),
                )}
              </select>
            </div>
          </div>
        </div>
      </div>
    );
  };

  const renderDays = () => {
    const days = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

    return (
      <div className="grid grid-cols-7 gap-2 text-center mb-2 border-b border-gray-500">
        {days.map((day) => (
          <div key={day} className="text-xs p-2 rounded">
            {day}
          </div>
        ))}
      </div>
    );
  };

  const reducedErrorMessages = deduceFormFieldErrors(errorMessages, fieldError);

  const handleCheckboxSelect = (selectedDate: Date) => {
    if (isToday(selectedDate)) {
      setSelectedByDateOption("today");
    } else if (
      format(subDays(currentTodayDate, 1), constants.viewDOBFormat) === format(selectedDate, constants.viewDOBFormat)
    ) {
      setSelectedByDateOption("yesterday");
    } else if (
      format(addDays(currentTodayDate, 1), constants?.viewDOBFormat) === format(selectedDate, constants?.viewDOBFormat)
    ) {
      setSelectedByDateOption("tomorrow");
    } else {
      setSelectedByDateOption("custom");
    }
  };

  const renderCells = () => {
    const cells = [];
    let day = 1;

    // Calculate the first day of the current month using the selected year
    const firstDayOfMonth = new Date(selectedYear ?? new Date().getFullYear(), currentMonth.getMonth(), 1);
    // Calculate the last day of the current month
    const lastDayOfMonth = new Date(selectedYear ?? new Date().getFullYear(), currentMonth.getMonth() + 1, 0);

    // Calculate the number of days to display from the previous month
    const daysFromPrevMonth = firstDayOfMonth.getDay();

    // Render cells for the previous month
    for (let i = 0; i < daysFromPrevMonth; i++) {
      const prevMonthDate = subDays(firstDayOfMonth, daysFromPrevMonth - i);
      cells.push(
        <div key={`prev-${i}`} className="py-3 text-xs text-center text-gray-400 rounded cursor-not-allowed">
          {getDate(prevMonthDate)}
        </div>,
      );
    }

    // Render cells for the current month
    while (day <= lastDayOfMonth.getDate()) {
      const currentDate = new Date(selectedYear ?? new Date().getFullYear(), currentMonth.getMonth(), day);

      cells.push(
        <div
          key={`current-${day}`}
          onClick={() => {
            onSelectDate(currentDate);
            handleCheckboxSelect(currentDate);
            setCurrentSelectedDate(currentDate);
            toggleCalendar();
          }}
          className={`py-3 text-xs text-center rounded cursor-pointer 
           
            ${
              currentDate.toDateString() === selectedDate.toDateString()
                ? "border border-black text-black"
                : "hover:bg-black hover:text-white"
            } ${
              getDate(currentSelectedDate) === day ? "bg-black shadow-md text-white" : "hover:bg-black hover:text-white"
            } `}
        >
          {day}
        </div>,
      );
      day++;
    }

    // Render cells for the next month to fill the grid
    const remainingDays = 7 - (cells.length % 7);
    for (let i = 1; i <= remainingDays; i++) {
      const nextMonthDate = addDays(lastDayOfMonth, i);
      cells.push(
        <div key={`next-${i}`} className="py-3 text-xs text-center text-gray-400 rounded cursor-not-allowed">
          {getDate(nextMonthDate)}
        </div>,
      );
    }

    return <div className="grid grid-cols-7 gap-2">{cells}</div>;
  };

  return (
    <>
      <div className="relative rounded-md 	">
        {label && <span className="absolute text-xs -top-4 left-1 text-gray-500">{label}</span>}
        <div className="flex items-center gap-2">
          <div className="cursor-pointer" onClick={toggleCalendar}>
            <CalendarDaysIcon className="w-5 h-5 text-blue-700 " />
          </div>
          <div className="flex items-center gap-1 border p-2 rounded-lg">
            <p onClick={prevWeekHandelFunction} className="cursor-pointer">
              <ChevronLeftIcon className="h-5 w-5" />
            </p>
            <p onClick={toggleCalendar} className="cursor-pointer">
              {displayDateString}
            </p>
            <p onClick={nextWeekHandelFunction} className="cursor-pointer">
              <ChevronRightIcon className="h-5 w-5" />
            </p>
          </div>
        </div>
      </div>
      {showTypedErrors && <FormFieldErrors errors={reducedErrorMessages} />}
      <Transition show={isOpen} as={React.Fragment}>
        <Dialog as="div" className="fixed inset-0 z-50 overflow-y-auto" onClose={() => toggleCalendar()} static>
          <div className="flex flex-col items-center justify-center min-h-screen px-4 text-center w-full">
            <Transition.Child
              as={React.Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0"
              enterTo="opacity-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100"
              leaveTo="opacity-0"
            >
              <Dialog.Overlay className="fixed inset-0 bg-black bg-opacity-30" />
            </Transition.Child>

            {/* This is the modal container */}
            <Transition.Child
              as={React.Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 translate-y-4"
              enterTo="opacity-100 translate-y-0"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 translate-y-0"
              leaveTo="opacity-0 translate-y-4"
            >
              <div
                className={clsx(
                  "bg-white   max-w-2xl  opacity-100 overflow-hidden rounded shadow-xl text-left transform transition-all translate-y-0 ",
                  !showLeftNav ? "" : "grid grid-cols-6  p-6 w-full ",
                )}
              >
                {showLeftNav && (
                  <div className="col-span-2">
                    <p className="text-left font-semibold">By Date</p>
                    <div className="flex flex-col pt-6 justify-start items-start">
                      {byDateFilterOptions.map((option) => {
                        return (
                          <div className="relative flex items-start">
                            <div className="flex h-6 items-center">
                              <input
                                id="today"
                                checked={selectedByDateOption === option.id}
                                onChange={() => {
                                  setSelectedByDateOption(option.id as ByDateOptionFilter);
                                  option.callbackFunc();
                                }}
                                aria-describedby="offers-description"
                                name="offers"
                                type="checkbox"
                                className="h-4 w-4 rounded border-gray-300 text-black focus:ring-black"
                              />
                            </div>
                            <div className="ml-3 text-sm leading-6">
                              <label
                                htmlFor="offers"
                                className={clsx(
                                  "font-normal text-xs text-gray-700",
                                  selectedByDateOption === option.id && "text-gray-800 font-semibold",
                                )}
                              >
                                {option.title}
                              </label>{" "}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}
                <div className="col-span-4">
                  <div className="inline-block w-full max-w-md p-6  overflow-hidden text-left align-middle transition-all transform bg-white shadow-xl rounded">
                    {renderHeader()}
                    {renderDays()}
                    {renderCells()}
                  </div>
                </div>
              </div>
            </Transition.Child>
          </div>
        </Dialog>
      </Transition>
    </>
  );
};

export default RotaCalendarComponent;
